import Seo from "@/components/Seo";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ExternalLink, Briefcase, Globe2, Wifi, ChevronLeft, ChevronRight, X, Sparkles, BadgeCheck, GraduationCap, Share2, Linkedin, Link2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PromoCodeField from "@/components/PromoCodeField";
import TrendingSkills from "@/components/TrendingSkills";
import { useConfig } from "@/lib/useConfig";
import { buildJobPostingLd } from "@/lib/jobPostingLd";
import ShareMenu from "@/components/ShareMenu";
import { jobShareText, jobShareUrl } from "@/lib/jobShare";

const CATEGORIES = ["All Categories", "ML Engineer", "Data Scientist", "AI Researcher", "AI Product Manager",
  "NLP Engineer", "Computer Vision Engineer", "AI Infrastructure", "DevOps AI", "Prompt Engineer",
  "AI Safety", "Full Stack AI"];
const JOB_TYPES = ["All Types", "Full-time", "Part-time", "Contract", "Internship", "Freelance"];
const PER_PAGE = 20;

const AVATAR_COLORS = ["#00F0FF", "#8B5CF6", "#22C55E", "#F59E0B", "#EF4444", "#3B82F6", "#14B8A6", "#EC4899"];

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min ago`;
  if (diff < 86400) { const h = Math.floor(diff / 3600); return `${h} hour${h > 1 ? "s" : ""} ago`; }
  const d = Math.floor(diff / 86400);
  return `${d} day${d > 1 ? "s" : ""} ago`;
};

const fmtSalary = (j) => {
  if (j.salary_text) return j.salary_text;
  if (j.salary_min) {
    const sym = { USD: "$", EUR: "€", GBP: "£" }[j.salary_currency] || (j.salary_currency ? j.salary_currency + " " : "");
    const k = (n) => (n >= 1000 ? `${Math.round(n / 1000)}k` : n);
    return j.salary_max && j.salary_max !== j.salary_min
      ? `${sym}${k(j.salary_min)} - ${sym}${k(j.salary_max)}`
      : `${sym}${k(j.salary_min)}`;
  }
  return "Not disclosed";
};

const CompanyAvatar = ({ job }) => {
  const [failed, setFailed] = useState(false);
  const color = AVATAR_COLORS[(job.company || "?").charCodeAt(0) % AVATAR_COLORS.length];
  if (job.company_logo && !failed) {
    return <img src={job.company_logo} alt={job.company} onError={() => setFailed(true)}
      className="w-12 h-12 rounded-lg object-cover bg-white/5 shrink-0" />;
  }
  return (
    <div className="w-12 h-12 rounded-lg flex items-center justify-center font-display font-bold text-lg shrink-0"
      style={{ backgroundColor: `${color}22`, color }}>
      {(job.company || "?").charAt(0).toUpperCase()}
    </div>
  );
};

const CAT_KEYWORDS = [
  ["Prompt Engineering", ["prompt"]],
  ["NLP", ["nlp", "natural language", "language model", "llm"]],
  ["Computer Vision", ["vision", "image", "opencv"]],
  ["Generative AI", ["generative", "gen ai", "genai", "diffusion"]],
  ["Deep Learning", ["deep learning", "neural"]],
  ["MLOps", ["mlops", "ml ops", "platform", "infrastructure", "devops"]],
  ["Data Science", ["data scientist", "data science", "analyst", "analytics"]],
  ["AI Safety", ["safety", "alignment", "governance"]],
  ["AI for Business", ["product", "manager", "strategy", "consultant", "marketing"]],
  ["Machine Learning", ["machine learning", "ml engineer", "ai engineer", "research"]],
];
function courseCategoryForJob(job) {
  const hay = `${job.title || ""} ${(job.tags || []).join(" ")}`.toLowerCase();
  for (const [cat, kws] of CAT_KEYWORDS) {
    if (kws.some((k) => hay.includes(k))) return cat;
  }
  return "AI Fundamentals";
}

const JobCard = ({ job, i }) => {
  const open = () => window.open(job.apply_url, "_blank", "noopener,noreferrer");
  const courseCat = courseCategoryForJob(job);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(i, 12) * 0.02 }}
      onClick={open} data-testid={`job-card-${job.id}`}
      className={`card-surface rounded-xl p-6 cursor-pointer transition-colors flex gap-4 ${job.featured ? "border-[#00F0FF]/50 bg-[#00F0FF]/[0.04] hover:border-[#00F0FF]/70" : "hover:border-[#00F0FF]/40"}`}>
      <CompanyAvatar job={job} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg leading-snug text-white truncate" data-testid={`job-title-${job.id}`}>{job.title}</h3>
            <p className="text-sm text-[#94A3B8] mt-0.5">{job.company}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {job.featured && (
              <span className="font-mono-tech text-[10px] uppercase px-2 py-1 rounded bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/40" data-testid={`job-featured-${job.id}`}>★ Featured</span>
            )}
            {job.curated && (
              <span className="font-mono-tech text-[10px] uppercase px-2 py-1 rounded bg-[#22C55E]/15 text-[#4ade80] border border-[#22C55E]/40 flex items-center gap-1" data-testid={`job-direct-${job.id}`}><BadgeCheck className="w-3 h-3" /> Direct</span>
            )}
            <span className="font-mono-tech text-[10px] uppercase px-2 py-1 rounded border border-white/10 text-[#94A3B8]"
              data-testid={`job-source-${job.id}`}>{job.source}</span>
            <ShareMenu url={jobShareUrl(job)} text={jobShareText(job)} testId={`job-share-${job.id}`} trackId={job.id} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-xs text-[#94A3B8] font-mono-tech">
          <span className="flex items-center gap-1"><span className="text-sm">{job.country_flag}</span> {job.location}</span>
          {job.is_remote && (
            <span className="flex items-center gap-1 text-[#4ade80]"><Wifi className="w-3.5 h-3.5" /> Remote</span>
          )}
          <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {job.job_type}</span>
          <span className="text-[#00F0FF] font-semibold">{fmtSalary(job)}</span>
          <span className="text-[#64748B]">{timeAgo(job.published_at)}</span>
        </div>

        {job.tags && job.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {job.tags.slice(0, 3).map((t, k) => (
              <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#94A3B8] capitalize">{t}</span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-white/10">
          <Link to={`/courses?category=${encodeURIComponent(courseCat)}`} onClick={(e) => e.stopPropagation()}
            data-testid={`job-courses-${job.id}`}
            className="inline-flex items-center gap-1.5 text-xs text-[#fbbf24] hover:underline font-medium">
            <GraduationCap className="w-3.5 h-3.5" /> Courses for this role
          </Link>
          <a href={job.apply_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
            data-testid={`job-apply-${job.id}`}
            className="inline-flex items-center gap-1.5 text-sm bg-[#22C55E] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition">
            Apply Now <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const inputCls = "w-full bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none";

const CountUp = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!value) { setDisplay(0); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display}</>;
};

const JOB_TIERS = [
  { key: "basic", name: "Basic Listing", price: 29, features: ["Standard placement on the jobs board", "30 days live", "Listed in relevant category and country filter"] },
  { key: "featured", name: "Featured Listing", price: 49, popular: true, features: ["Top of the jobs board for your category", "Bold highlighted card", "Featured badge on listing", "30 days live", "Included in one WorldAI Weekly newsletter"] },
  { key: "premium", name: "Premium Listing", price: 79, features: ["Homepage featured placement", "Top of all search results", "Featured badge", "45 days live", "Included in two WorldAI Weekly newsletters", "Social media mention"] },
];

const PostJobModal = ({ open, onClose }) => {
  const empty = { title: "", company: "", company_website: "", location: "", country: "",
    salary_from: "", salary_to: "", currency: "USD", job_type: "Full-time", is_remote: false,
    description: "", skills: "", apply_url: "", contact_email: "" };
  const [form, setForm] = useState(empty);
  const [step, setStep] = useState("tier"); // tier | form
  const [tier, setTier] = useState(null);
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);
  const { paymentsEnabled } = useConfig();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const effPrice = tier ? (promo ? +(tier.price * (1 - promo.discount_pct / 100)).toFixed(2) : tier.price) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/checkout", { form, tier: paymentsEnabled ? tier.key : "basic", promo_code: promo?.code, origin_url: window.location.origin });
      if (data.free || !data.url) {
        toast.success("Submitted! We'll review your job within 24 hours and email you when it's live — you're on the WorldAI Weekly too.");
        close();
      } else {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setBusy(false);
    }
  };
  const close = () => { setForm(empty); setStep("tier"); setTier(null); setPromo(null); setBusy(false); onClose(); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="post-job-modal">
      <div className="card-surface rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={close} className="absolute top-4 right-4 text-[#94A3B8] hover:text-white" data-testid="post-job-close"><X className="w-5 h-5" /></button>
        {paymentsEnabled && step === "tier" ? (
          <div data-testid="post-job-tiers">
            <h3 className="font-display font-bold text-2xl mb-1">Post a Job</h3>
            <p className="text-sm text-[#94A3B8] mb-6">Choose a listing plan. Payment is required before your listing is reviewed.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {JOB_TIERS.map((t) => (
                <div key={t.key} data-testid={`tier-card-${t.key}`}
                  className={`rounded-xl p-5 border flex flex-col ${t.popular ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/10 bg-[#0e1424]"}`}>
                  {t.popular && <span className="font-mono-tech text-[10px] uppercase text-[#00F0FF] mb-2">Most popular</span>}
                  <h4 className="font-display font-bold text-lg">{t.name}</h4>
                  <div className="font-display font-extrabold text-3xl text-[#00F0FF] my-2">${t.price}</div>
                  <ul className="space-y-1.5 text-xs text-[#94A3B8] flex-1 mb-4">
                    {t.features.map((f, i) => <li key={i} className="flex gap-1.5"><span className="text-[#22C55E]">✓</span> {f}</li>)}
                  </ul>
                  <button onClick={() => { setTier(t); setStep("form"); }} data-testid={`tier-select-${t.key}`}
                    className={`w-full font-semibold px-4 py-2.5 rounded-md transition text-sm ${t.popular ? "bg-[#00F0FF] text-[#04121a] hover:brightness-110" : "border border-white/15 hover:border-[#00F0FF]/60"}`}>
                    Choose {t.name.split(" ")[0]}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <h3 className="font-display font-bold text-2xl">Post a Job</h3>
              {paymentsEnabled && (
                <button onClick={() => { setStep("tier"); setPromo(null); }} data-testid="pj-change-tier" className="text-xs text-[#00F0FF] hover:underline">← Change plan</button>
              )}
            </div>
            <p className="text-sm text-[#94A3B8] mb-6">
              {paymentsEnabled && tier ? (
                <>
                  <span className="text-white font-medium">
                    {tier.name} — {promo ? (<><span className="line-through text-[#64748B]">${tier.price}</span> <span className="text-[#FBBF24]">${effPrice}</span></>) : <>${tier.price}</>}.
                  </span> Your listing will be reviewed and published within 24 hours after payment.
                </>
              ) : (
                <><span className="text-[#4ade80] font-medium">Free to post.</span> Every listing is reviewed by our team and published within 24 hours — no payment required.</>
              )}
            </p>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Job Title *</label>
                <input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="pj-title" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Company Name *</label>
                <input required value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="pj-company" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Company Website</label>
                <input value={form.company_website} onChange={(e) => set("company_website", e.target.value)} className={inputCls} placeholder="https://" data-testid="pj-website" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Location</label>
                <input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} data-testid="pj-location" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Country</label>
                <input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="pj-country" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Salary From</label>
                <input value={form.salary_from} onChange={(e) => set("salary_from", e.target.value)} className={inputCls} placeholder="100000" data-testid="pj-salary-from" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Salary To</label>
                <input value={form.salary_to} onChange={(e) => set("salary_to", e.target.value)} className={inputCls} placeholder="150000" data-testid="pj-salary-to" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Currency</label>
                <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls} data-testid="pj-currency">
                  {["USD", "EUR", "GBP", "INR", "CAD", "AUD"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Job Type</label>
                <select value={form.job_type} onChange={(e) => set("job_type", e.target.value)} className={inputCls} data-testid="pj-jobtype">
                  {JOB_TYPES.slice(1).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" checked={form.is_remote} onChange={(e) => set("is_remote", e.target.checked)} className="w-4 h-4 accent-[#00F0FF]" data-testid="pj-remote" id="pj-remote" />
                <label htmlFor="pj-remote" className="text-sm text-[#94A3B8]">Remote position</label>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Job Description *</label>
                <textarea required rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="pj-description" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Skills (comma separated)</label>
                <input value={form.skills} onChange={(e) => set("skills", e.target.value)} className={inputCls} placeholder="Python, PyTorch, LLMs" data-testid="pj-skills" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Application URL *</label>
                <input required value={form.apply_url} onChange={(e) => set("apply_url", e.target.value)} className={inputCls} placeholder="https://" data-testid="pj-apply-url" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Contact Email *</label>
                <input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} data-testid="pj-contact-email" />
              </div>
              <div className="md:col-span-2 mt-2">
                <PromoCodeField onApplied={setPromo} />
              </div>
              <div className="md:col-span-2 mt-2">
                <button type="submit" disabled={busy}
                  className="w-full bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition disabled:opacity-50" data-testid="pj-submit">
                  {busy ? (paymentsEnabled ? "Redirecting to secure payment…" : "Submitting…") : (paymentsEnabled ? `Continue to Payment — $${effPrice}` : "Submit Job for Review")}
                </button>
                <p className="text-xs text-[#64748B] text-center mt-2">
                  {paymentsEnabled ? "Secure payment via Stripe. Your listing is reviewed after payment is confirmed." : "Free while we're in pre-launch. Every listing is reviewed before it goes live."}
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default function Jobs() {
  const [allJobs, setAllJobs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [topRecruiters, setTopRecruiters] = useState([]);
  const { paymentsEnabled } = useConfig();

  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("search") || "");
  const [country, setCountry] = useState(() => new URLSearchParams(window.location.search).get("country") || "All Countries");
  const [category, setCategory] = useState("All Categories");
  const [jobType, setJobType] = useState("All Types");
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [sort, setSort] = useState("recent");
  const [company, setCompany] = useState(() => new URLSearchParams(window.location.search).get("company") || "All Companies");
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get("/live-jobs").then((r) => {
      setAllJobs(r.data.jobs || []);
      setCountries(r.data.countries || []);
      setLastUpdated(r.data.last_updated);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(1); }, [search, country, category, jobType, remoteOnly, sort, company]);
  useEffect(() => { api.get("/recruiters/top").then((r) => setTopRecruiters(r.data || [])).catch(() => {}); }, []);

  // Sync company filter to the URL (bookmarkable / shareable).
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (company !== "All Companies") params.set("company", company);
    else params.delete("company");
    setSearchParams(params, { replace: true });
  }, [company]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasActiveFilters = !!search || country !== "All Countries" || category !== "All Categories" || jobType !== "All Types" || remoteOnly || company !== "All Companies";
  const featuredJobs = useMemo(() => allJobs.filter((j) => j.featured).slice(0, 3), [allJobs]);
  const showFeaturedStrip = featuredJobs.length > 0 && !hasActiveFilters;

  const filtered = useMemo(() => {
    let list = allJobs.filter((j) => {
      if (showFeaturedStrip && j.featured) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!j.title.toLowerCase().includes(s) && !j.company.toLowerCase().includes(s)) return false;
      }
      if (country !== "All Countries" && j.country !== country) return false;
      if (category !== "All Categories" && j.category !== category) return false;
      if (jobType !== "All Types" && j.job_type !== jobType) return false;
      if (remoteOnly && !j.is_remote) return false;
      if (company !== "All Companies" && j.source !== company) return false;
      return true;
    });
    if (sort === "salary") list = [...list].sort((a, b) => (b.salary_max || b.salary_min || 0) - (a.salary_max || a.salary_min || 0));
    else if (sort === "company") list = [...list].sort((a, b) => a.company.localeCompare(b.company));
    else list = [...list].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
    return list;
  }, [allJobs, search, country, category, jobType, remoteOnly, sort, company, showFeaturedStrip]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageJobs = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // JobPosting structured data for the jobs shown on this page (Google Jobs rich results).
  const jobsJsonLd = useMemo(() => {
    const items = [...(showFeaturedStrip ? featuredJobs : []), ...pageJobs]
      .map(buildJobPostingLd).filter(Boolean);
    return items.length ? items : null;
  }, [pageJobs, featuredJobs, showFeaturedStrip]);

  const topCountries = useMemo(() => {
    const map = {};
    allJobs.forEach((j) => {
      if (!j.country) return;
      if (!map[j.country]) map[j.country] = { country: j.country, flag: j.country_flag, count: 0 };
      map[j.country].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 15);
  }, [allJobs]);

  const reset = () => { setSearch(""); setCountry("All Countries"); setCategory("All Categories"); setJobType("All Types"); setRemoteOnly(false); setSort("recent"); setCompany("All Companies"); };

  const curatedCompanies = useMemo(() => {
    const map = {};
    allJobs.forEach((j) => {
      if (!j.curated || !j.source) return;
      map[j.source] = (map[j.source] || 0) + 1;
    });
    return Object.entries(map).map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [allJobs]);

  return (
    <div data-testid="jobs-page">
      <Seo
        title={company !== "All Companies" ? `AI Jobs at ${company} — WorldAIInfo` : "AI Jobs — ML & AI Roles Sourced from Top Labs | WorldAIInfo"}
        description={company !== "All Companies"
          ? `Browse open AI roles at ${company}, sourced directly from their official careers page. Apply directly on WorldAIInfo.`
          : "Browse live AI & machine-learning jobs sourced directly from OpenAI, Anthropic, Google DeepMind, Databricks and more. Apply directly to employers."}
        canonicalPath={company !== "All Companies" ? `/jobs?company=${encodeURIComponent(company)}` : "/jobs"}
        jsonLd={jobsJsonLd}
      />
      <PostJobModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Header */}
      <section className="grid-bg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-10">
          <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-4 py-1.5 mb-5 bg-[#111827]">
            <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
            <span className="font-mono-tech text-xs text-[#94A3B8]">LIVE JOB FEED</span>
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">Global AI Jobs</h1>
          <p className="mt-4 text-base md:text-lg text-[#94A3B8] max-w-2xl">
            Real AI jobs from companies worldwide — click Apply to go directly to the employer.
          </p>

          <div className="mt-8 flex flex-wrap gap-8">
            <div><div className="font-display font-bold text-3xl text-[#00F0FF]" data-testid="stat-total-jobs"><CountUp value={allJobs.length} /></div><div className="font-mono-tech text-xs uppercase text-[#94A3B8]">Live Jobs</div></div>
            <div><div className="font-display font-bold text-3xl text-[#00F0FF]" data-testid="stat-countries"><CountUp value={countries.length} /></div><div className="font-mono-tech text-xs uppercase text-[#94A3B8]">Countries</div></div>
            <div>
              <div className="flex items-center gap-2 font-mono-tech text-sm text-[#94A3B8]">
                <span className="w-2 h-2 rounded-full bg-[#10B981] live-dot" /> Updated {timeAgo(lastUpdated) || "just now"}
              </div>
              <div className="font-mono-tech text-xs uppercase text-[#94A3B8] mt-1">Auto-refreshed every 6h</div>
            </div>
          </div>

          {/* Top countries flag row */}
          {topCountries.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="top-countries-row">
              <span className="font-mono-tech text-xs uppercase text-[#64748B] mr-1">Hiring in</span>
              {topCountries.map((c) => (
                <button key={c.country} onClick={() => setCountry(c.country)} title={`${c.country} · ${c.count} jobs`}
                  data-testid={`country-chip-${c.country}`}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-mono-tech transition ${country === c.country ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/10 text-[#94A3B8] hover:border-white/25"}`}>
                  <span className="text-sm leading-none">{c.flag}</span>
                  <span>{c.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Featured banner */}
          <div className="mt-8 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="hiring-banner">
            <div className="flex items-center gap-3">
              <Briefcase className="w-6 h-6 text-[#fbbf24] shrink-0" />
              <p className="text-sm text-[#fbbf24]"><span className="font-semibold">Hiring AI talent?</span> {paymentsEnabled ? "Post your job to our growing community — from $29 per listing." : "Post your job to our growing community — free while we're in pre-launch."}</p>
            </div>
            <div className="flex items-center gap-4">
              {paymentsEnabled && (
                <Link to="/recruiters" data-testid="recruiter-plans-link" className="text-sm font-medium text-[#fbbf24] hover:underline whitespace-nowrap">Recruiter plans →</Link>
              )}
              <button onClick={() => setModalOpen(true)} data-testid="post-job-banner-btn"
                className="bg-[#fbbf24] text-[#1a1204] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 transition whitespace-nowrap">{paymentsEnabled ? "Post a Job" : "Post a Job — Free"}</button>
            </div>
          </div>

          {topRecruiters.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="top-recruiters-strip">
              <span className="font-mono-tech text-xs uppercase text-[#64748B] mr-1 inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-[#00F0FF]" /> Top Recruiters</span>
              {topRecruiters.map((r, i) => (
                <span key={i} data-testid="top-recruiter-chip"
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 text-xs text-[#00F0FF] font-mono-tech">
                  {r.company_name} <span className="text-[10px] text-[#94A3B8]">· {r.badge}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Curated-sources trust strip */}
      <div className="border-b border-white/10 bg-[#0B0F19]" data-testid="curated-trust-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="font-mono-tech text-xs uppercase text-[#4ade80] inline-flex items-center gap-1.5 shrink-0">
            <BadgeCheck className="w-4 h-4" /> Sourced directly from
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { name: "OpenAI", domain: "openai.com" },
              { name: "Anthropic", domain: "anthropic.com" },
              { name: "Google DeepMind", domain: "deepmind.google" },
              { name: "Databricks", domain: "databricks.com" },
              { name: "Mistral AI", domain: "mistral.ai" },
              { name: "Cohere", domain: "cohere.com" },
              { name: "Perplexity", domain: "perplexity.ai" },
              { name: "Hugging Face", domain: "huggingface.co" },
            ].map((c) => (
              <span key={c.name} data-testid={`curated-brand-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.03] text-xs text-[#CBD5E1]">
                <img src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=64`} alt=""
                  width={16} height={16} loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="w-4 h-4 rounded-sm" />
                {c.name}
              </span>
            ))}
            <span className="text-xs text-[#64748B] font-mono-tech">& other leading AI labs</span>
          </div>
        </div>
      </div>


      {/* Sticky filters */}
      <div className="sticky top-16 z-30 bg-[#0B0F19]/95 backdrop-blur border-b border-white/10" data-testid="jobs-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search job title or company"
              className="w-full bg-[#111827] border border-white/10 rounded-md pl-10 pr-4 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none" data-testid="jobs-search" />
          </div>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none" data-testid="jobs-country">
            <option>All Countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none" data-testid="jobs-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {curatedCompanies.length > 0 && (
            <select value={company} onChange={(e) => setCompany(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none" data-testid="jobs-company">
              <option value="All Companies">All Companies</option>
              {curatedCompanies.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </select>
          )}
          <select value={jobType} onChange={(e) => setJobType(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none" data-testid="jobs-type">
            {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={() => setRemoteOnly(!remoteOnly)} data-testid="jobs-remote-toggle"
            className={`font-mono-tech text-xs px-4 py-2.5 rounded border transition ${remoteOnly ? "border-[#4ade80] text-[#4ade80] bg-[#4ade80]/10" : "border-white/10 text-[#94A3B8] hover:border-white/25"}`}>
            Remote Only
          </button>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none" data-testid="jobs-sort">
            <option value="recent">Most Recent</option>
            <option value="salary">Highest Salary</option>
            <option value="company">Company A-Z</option>
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <TrendingSkills onSelect={(s) => setSearch(s)} />
        {showFeaturedStrip && (
          <div className="mb-8" data-testid="featured-jobs-strip">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[#00F0FF]">★</span>
              <h2 className="font-display font-bold text-xl">Featured Jobs</h2>
            </div>
            <div className="flex flex-col gap-4">
              {featuredJobs.map((j, i) => <JobCard key={j.id} job={j} i={i} />)}
            </div>
            <div className="border-t border-white/10 mt-8" />
          </div>
        )}
        <div className="flex items-center justify-between gap-4 mb-5">
          <p className="text-sm text-[#94A3B8] font-mono-tech" data-testid="jobs-result-count">
            {loading ? "Loading jobs…" : `${filtered.length} job${filtered.length !== 1 ? "s" : ""} found`}
          </p>
          {company !== "All Companies" && (
            <div className="relative">
              <button onClick={() => setShareOpen((o) => !o)} data-testid="jobs-share-btn"
                className="inline-flex items-center gap-1.5 text-xs font-mono-tech px-3 py-2 rounded-md border border-[#00F0FF]/30 text-[#00F0FF] bg-[#00F0FF]/5 hover:bg-[#00F0FF]/10 transition">
                <Share2 className="w-3.5 h-3.5" /> Share {company} jobs
              </button>
              {shareOpen && (() => {
                const backend = process.env.REACT_APP_BACKEND_URL;
                const shareUrl = `${backend}/api/share/jobs?company=${encodeURIComponent(company)}`;
                const cleanUrl = `${window.location.origin}/jobs?company=${encodeURIComponent(company)}`;
                const text = `AI Jobs at ${company} — sourced directly from their official careers page on WorldAIInfo`;
                return (
                  <div className="absolute right-0 mt-2 z-40 w-52 rounded-lg border border-white/10 bg-[#111827] p-2 shadow-xl" data-testid="jobs-share-menu">
                    <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`}
                      target="_blank" rel="noopener noreferrer" data-testid="jobs-share-x"
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#CBD5E1] hover:bg-white/5">
                      <X className="w-4 h-4" /> Share on X
                    </a>
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                      target="_blank" rel="noopener noreferrer" data-testid="jobs-share-linkedin"
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#CBD5E1] hover:bg-white/5">
                      <Linkedin className="w-4 h-4" /> Share on LinkedIn
                    </a>
                    <button onClick={() => { navigator.clipboard.writeText(cleanUrl); toast.success("Link copied"); setShareOpen(false); }}
                      data-testid="jobs-share-copy"
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#CBD5E1] hover:bg-white/5">
                      <Link2 className="w-4 h-4" /> Copy link
                    </button>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {!loading && filtered.length === 0 ? (
          <div className="text-center py-24" data-testid="jobs-empty">
            <Globe2 className="w-12 h-12 text-[#334155] mx-auto mb-4" />
            <p className="text-[#94A3B8] mb-5">No jobs found for these filters. Try adjusting your search.</p>
            <button onClick={reset} className="bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-2.5 rounded-md hover:brightness-110 transition" data-testid="jobs-reset">Reset Filters</button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {pageJobs.map((j, i) => <JobCard key={j.id} job={j} i={i} />)}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-10" data-testid="jobs-pagination">
                <button disabled={page === 1} onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="flex items-center gap-1 text-sm px-4 py-2 rounded-md border border-white/10 disabled:opacity-40 hover:border-[#00F0FF]/50 transition" data-testid="jobs-prev">
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="font-mono-tech text-sm text-[#94A3B8]">Page {page} of {totalPages}</span>
                <button disabled={page === totalPages} onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="flex items-center gap-1 text-sm px-4 py-2 rounded-md border border-white/10 disabled:opacity-40 hover:border-[#00F0FF]/50 transition" data-testid="jobs-next">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-[#64748B] leading-relaxed mt-16 max-w-3xl mx-auto text-center" data-testid="jobs-disclaimer">
          All job listings are sourced from Himalayas, Remotive, and directly from leading AI companies' official career pages (OpenAI, Anthropic, Google DeepMind, Databricks and more). Clicking Apply Now takes you directly to the employer or original job posting page. WorldAIInfo.com does not process applications or store personal applicant data. Job availability may change — always verify on the employer website.
        </p>
      </div>
    </div>
  );
}
